import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  SystemUpdate,
  SystemUpdatePage,
  SystemUpdateRead,
  SystemUpdateSaveRequest,
} from './system-updates.model';

/** cm-server-java `cmserver.systemupdate` — per-tenant "What's new" feed and its admin. */
@Injectable({ providedIn: 'root' })
export class SystemUpdatesService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = '/api/core/system-updates';

  /** Published updates the caller has not read, oldest first. */
  getUnread(): Observable<SystemUpdate[]> {
    return this.http.get<SystemUpdate[]>(`${this.baseUrl}/unread`).pipe(
      map((updates) => (updates ?? []).map(normalize)),
    );
  }

  /** Idempotent read receipt. */
  markRead(uuid: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${encodeURIComponent(uuid)}/read`, null);
  }

  /** Archive: published updates, newest first, flagged `read`. */
  getArchive(page: number, size: number): Observable<SystemUpdatePage> {
    return this.getPage(this.baseUrl, page, size);
  }

  /** Admin list: every update including drafts, with `readCount`. PM only. */
  getAdminList(page: number, size: number): Observable<SystemUpdatePage> {
    return this.getPage(`${this.baseUrl}/admin`, page, size);
  }

  create(request: SystemUpdateSaveRequest): Observable<SystemUpdate> {
    return this.http
      .post<SystemUpdate>(`${this.baseUrl}/admin`, toSaveBody(request))
      .pipe(map(normalize));
  }

  /** PUT replaces every field — the audience is always sent (`[]` = everyone), never omitted. */
  update(uuid: string, request: SystemUpdateSaveRequest): Observable<SystemUpdate> {
    return this.http
      .put<SystemUpdate>(`${this.baseUrl}/admin/${encodeURIComponent(uuid)}`, toSaveBody(request))
      .pipe(map(normalize));
  }

  delete(uuid: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/admin/${encodeURIComponent(uuid)}`);
  }

  publish(uuid: string): Observable<SystemUpdate> {
    return this.http
      .post<SystemUpdate>(`${this.baseUrl}/admin/${encodeURIComponent(uuid)}/publish`, null)
      .pipe(map(normalize));
  }

  unpublish(uuid: string): Observable<SystemUpdate> {
    return this.http
      .post<SystemUpdate>(`${this.baseUrl}/admin/${encodeURIComponent(uuid)}/unpublish`, null)
      .pipe(map(normalize));
  }

  /** Who read the update and when. PM only. */
  getReads(uuid: string): Observable<SystemUpdateRead[]> {
    return this.http
      .get<SystemUpdateRead[]>(`${this.baseUrl}/admin/${encodeURIComponent(uuid)}/reads`)
      .pipe(map((reads) => reads ?? []));
  }

  private getPage(url: string, page: number, size: number): Observable<SystemUpdatePage> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<SystemUpdatePage>(url, { params }).pipe(
      map((result) => ({
        ...result,
        content: (result?.content ?? []).map(normalize),
        totalElements: result?.totalElements ?? 0,
      })),
    );
  }
}

/** The request body exactly as Java reads it — every field present, `audienceRoles` never null. */
function toSaveBody(request: SystemUpdateSaveRequest): SystemUpdateSaveRequest {
  return {
    title: request.title,
    body: request.body,
    mediaAssetUuid: request.mediaAssetUuid ?? null,
    audienceRoles: [...(request.audienceRoles ?? [])],
  };
}

/** Defaults for the fields the list/unread endpoints may omit. */
function normalize(update: SystemUpdate): SystemUpdate {
  return {
    ...update,
    mediaAssetUuid: update.mediaAssetUuid ?? null,
    mediaType: update.mediaType ?? null,
    mediaUrl: update.mediaUrl ?? null,
    mediaThumbnailUrl: update.mediaThumbnailUrl ?? null,
    publishedAt: update.publishedAt ?? null,
    createdByUuid: update.createdByUuid ?? null,
    read: !!update.read,
    readCount: update.readCount ?? 0,
    audienceRoles: update.audienceRoles ?? [],
  };
}
